import { Component, OnInit, AfterViewInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import * as Chart from 'chart.js';
import { ToastrService } from 'ngx-toastr';
import { ProductionBusinessService } from 'src/app/services/production/production-business.service';
import { DashboardStats, ProductionKpiCard, ChartDataPoint, ProductionVsReturnPoint, WorkforceSegment, RecentActivityItem, QuickAction, ProductionDashboardData } from '../../../models/Production/dashboard';

@Component({
  selector: 'app-production-overview-dashboard',
  templateUrl: './production-overview-dashboard.component.html',
  styleUrls: ['./production-overview-dashboard.component.scss']
})
export class ProductionDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() actionTriggered = new EventEmitter<string>();

  stats: DashboardStats = {
    totalCompanies: 0,
    totalMarketCategories: 0,
    totalSheds: 0,
    totalProductions: 0,
    marketReturnProductions: 0,
    activeMachineOperators: 0,
    activeQualityCheckers: 0,
    activeGraders: 0
  };

  kpiCards: ProductionKpiCard[] = [
    { label: 'Total Companies', valueKey: 'totalCompanies', icon: 'fa-building', tone: 'primary' },
    { label: 'Market Categories', valueKey: 'totalMarketCategories', icon: 'fa-tags', tone: 'info' },
    { label: 'Total Sheds', valueKey: 'totalSheds', icon: 'fa-home', tone: 'purple' },
    { label: 'Total Productions', valueKey: 'totalProductions', icon: 'fa-industry', tone: 'success' },
    { label: 'Market Return Productions', valueKey: 'marketReturnProductions', icon: 'fa-undo', tone: 'danger' },
    { label: 'Active Machine Operators', valueKey: 'activeMachineOperators', icon: 'fa-cogs', tone: 'primary' },
    { label: 'Active Quality Checkers', valueKey: 'activeQualityCheckers', icon: 'fa-check-circle', tone: 'success' },
    { label: 'Active Graders', valueKey: 'activeGraders', icon: 'fa-star', tone: 'warning' }
  ];

  quickActions: QuickAction[] = [
    { label: 'Add Company', icon: 'fa-building', tone: 'primary', action: 'add-company', route: '/Companies' },
    { label: 'Add Shed', icon: 'fa-home', tone: 'purple', action: 'add-shed', route: '/Shed' },
    { label: 'Create Production', icon: 'fa-industry', tone: 'success', action: 'create-production', route: '/Production' },
    { label: 'Add Market Return', icon: 'fa-undo', tone: 'danger', action: 'add-market-return', route: '/MaterialReceiptReturn' },
    { label: 'Assign Operator', icon: 'fa-cogs', tone: 'info', action: 'assign-operator' },
    { label: 'Assign Quality Checker', icon: 'fa-check-circle', tone: 'success', action: 'assign-quality-checker' },
    { label: 'Assign Grader', icon: 'fa-star', tone: 'warning', action: 'assign-grader' }
  ];

  recentProductions: RecentActivityItem[] = [];
  recentMarketReturns: RecentActivityItem[] = [];
  recentQualityChecks: RecentActivityItem[] = [];

  activityTabs: Array<{ key: string; label: string }> = [
    { key: 'productions', label: 'Productions' },
    { key: 'returns', label: 'Market Returns' },
    { key: 'quality', label: 'Quality Checks' }
  ];
  activeActivityTab = 'productions';

  isLoading = true;

  private productionByShed: ChartDataPoint[] = [];
  private dailyProductionTrend: ChartDataPoint[] = [];
  private productionVsReturn: ProductionVsReturnPoint[] = [];
  private workforceDistribution: WorkforceSegment[] = [];
  private shedChart: any;
  private trendChart: any;
  private vsReturnChart: any;
  private workforceChart: any;

  private viewReady = false;

  constructor(
    private productionBusinessService: ProductionBusinessService,
    private toastr: ToastrService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.buildCharts();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }


 loadDashboard(): void {

  this.isLoading = true;
  console.log('Loading dashboard data...');

  this.productionBusinessService.productionDashboardService.GetDashboardData()
    .subscribe({

      next: (response: any) => {

        console.log('API Response:', response);

        if (!response) {
          this.isLoading = false;
          this.toastr.error('No response received from server.');
          return;
        }

        if (response._statusCode != 200) {
          this.isLoading = false;

          if (response._message) {
            this.toastr.error(response._message);
          } else {
            this.toastr.error('Unable to load dashboard.');
          }

          return;
        }

        if (!response._obj) {
          this.isLoading = false;
          this.toastr.error('No dashboard data found.');
          return;
        }

        var data: any = response._obj;

        console.log('Dashboard Data:', data);

        //==========================
        // Stats
        //==========================
        var stats = data.Stats || {};

        this.stats = {
          totalCompanies: stats.TotalCompanies || 0,
          totalMarketCategories: stats.TotalMarketCategories || 0,
          totalSheds: stats.TotalSheds || 0,
          totalProductions: stats.TotalProductions || 0,
          marketReturnProductions: stats.MarketReturnProductions || 0,
          activeMachineOperators: stats.ActiveMachineOperators || 0,
          activeQualityCheckers: stats.ActiveQualityCheckers || 0,
          activeGraders: stats.ActiveGraders || 0
        };

        //==========================
        // Production By Shed
        //==========================
        this.productionByShed = [];

        if (data.ProductionByShed) {
          data.ProductionByShed.forEach((item: any) => {

            this.productionByShed.push({
              label: item.Label,
              value: item.Value
            });

          });
        }

        //==========================
        // Daily Production Trend
        //==========================
        this.dailyProductionTrend = [];

        if (data.DailyProductionTrend) {
          data.DailyProductionTrend.forEach((item: any) => {

            this.dailyProductionTrend.push({
              label: item.Label,
              value: item.Value
            });

          });
        }

        //==========================
        // Production Vs Return
        //==========================
        this.productionVsReturn = [];

        if (data.ProductionVsReturn) {

          data.ProductionVsReturn.forEach((item: any) => {

            this.productionVsReturn.push({
              label: item.Label,
              production: item.Production,
              marketReturn: item.MarketReturn
            });

          });
        }

        //==========================
        // Workforce
        //==========================
        this.workforceDistribution = [];

        if (data.WorkforceDistribution) {

          data.WorkforceDistribution.forEach((item: any) => {

            this.workforceDistribution.push({
              label: item.Label,
              value: item.Value,
              color: item.Color
            });

          });
        }

        //==========================
        // Recent Productions
        //==========================
        this.recentProductions = [];

        if (data.RecentProductions) {

          data.RecentProductions.forEach((item: any) => {

            this.recentProductions.push({
              referenceNo: item.ReferenceNo,
              date: item.Date,
              status: item.Status,
              quantity: item.Quantity,
              type: item.Type
            });

          });
        }

        //==========================
        // Recent Market Returns
        //==========================
        this.recentMarketReturns = [];

        if (data.RecentMarketReturns) {

          data.RecentMarketReturns.forEach((item: any) => {

            this.recentMarketReturns.push({
              referenceNo: item.ReferenceNo,
              date: item.Date,
              status: item.Status,
              quantity: item.Quantity,
              type: item.Type
            });

          });
        }

        //==========================
        // Recent Quality Checks
        //==========================
        this.recentQualityChecks = [];

        if (data.RecentQualityChecks) {

          data.RecentQualityChecks.forEach((item: any) => {

            this.recentQualityChecks.push({
              referenceNo: item.ReferenceNo,
              date: item.Date,
              status: item.Status,
              quantity: item.Quantity,
              type: item.Type
            });

          });
        }

        console.log("Stats :", this.stats);
        console.log("ProductionByShed :", this.productionByShed);
        console.log("Trend :", this.dailyProductionTrend);
        console.log("VsReturn :", this.productionVsReturn);
        console.log("Workforce :", this.workforceDistribution);

        this.isLoading = false;

        setTimeout(() => {
          this.buildCharts();
        }, 100);

      },

      error: (err: any) => {

        console.error(err);

        this.isLoading = false;

        if (err && err.error && err.error._message) {
          this.toastr.error(err.error._message);
        }
        else if (err && err.message) {
          this.toastr.error(err.message);
        }
        else {
          this.toastr.error('Unable to load dashboard.');
        }

      }

    });

}


  getKpiValue(card: ProductionKpiCard): string {
    const value = this.stats[card.valueKey];
    return `${value}${card.suffix || ''}`;
  }


  get activeActivityRows(): RecentActivityItem[] {
    if (this.activeActivityTab === 'returns') {
      return this.recentMarketReturns;
    }
    if (this.activeActivityTab === 'quality') {
      return this.recentQualityChecks;
    }
    return this.recentProductions;
  }


  setActivityTab(key: string): void {
    this.activeActivityTab = key;
  }


  statusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (['completed', 'approved', 'passed'].indexOf(s) !== -1) {
      return 'status-success';
    }
    if (['in progress', 'review', 'pending'].indexOf(s) !== -1) {
      return 'status-info';
    }
    if (['on hold'].indexOf(s) !== -1) {
      return 'status-warning';
    }
    if (['rejected', 'failed'].indexOf(s) !== -1) {
      return 'status-danger';
    }
    return 'status-default';
  }


  onQuickAction(action: QuickAction): void {
    this.actionTriggered.emit(action.action);
    if (action.route) {
      this.router.navigate([action.route]);
    } else {
      this.toastr.info(action.label);
    }
  }

  private buildCharts(): void {
    if (!this.viewReady || this.isLoading) {
      return;
    }

    setTimeout(() => {
      this.buildShedChart();
      this.buildTrendChart();
      this.buildVsReturnChart();
      this.buildWorkforceChart();
    }, 0);
  }


  private buildShedChart(): void {
    const canvas = document.getElementById('prodShedChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('prodShedChart canvas not found');
      return;
    }
    if (this.shedChart) {
      this.shedChart.destroy();
    }

    const labels = this.productionByShed.map(p => p.label);
    const data = this.productionByShed.map(p => p.value);

    if (data.length === 0) {
      this.shedChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: ['No Data'],
          datasets: [{
            label: 'Units Produced',
            data: [0],
            backgroundColor: '#e0e0e0',
            borderColor: '#bdbdbd',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false }
        }
      });
      return;
    }

    this.shedChart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Units Produced',
          data: data,
          backgroundColor: '#009688',
          borderColor: '#00796b',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    });
  }


  private buildTrendChart(): void {
    const canvas = document.getElementById('prodTrendChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('prodTrendChart canvas not found');
      return;
    }
    if (this.trendChart) {
      this.trendChart.destroy();
    }

    const labels = this.dailyProductionTrend.map(p => p.label);
    const data = this.dailyProductionTrend.map(p => p.value);

    if (data.length === 0) {
      this.trendChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: ['No Data'],
          datasets: [{
            label: 'Daily Production',
            data: [0],
            borderColor: '#e0e0e0',
            backgroundColor: 'rgba(224, 224, 224, 0.12)',
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false }
        }
      });
      return;
    }

    this.trendChart = new Chart(canvas.getContext('2d'), {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Daily Production',
          data: data,
          borderColor: '#4f46e5',
          backgroundColor: 'rgba(79, 70, 229, 0.12)',
          fill: true,
          lineTension: 0.3,
          pointBackgroundColor: '#4f46e5'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    });
  }


  private buildVsReturnChart(): void {
    const canvas = document.getElementById('prodVsReturnChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('prodVsReturnChart canvas not found');
      return;
    }
    if (this.vsReturnChart) {
      this.vsReturnChart.destroy();
    }

    const labels = this.productionVsReturn.map(p => p.label);
    const productionData = this.productionVsReturn.map(p => p.production);
    const returnData = this.productionVsReturn.map(p => p.marketReturn);

    if (labels.length === 0) {
      this.vsReturnChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: ['No Data'],
          datasets: [
            {
              label: 'Production',
              data: [0],
              backgroundColor: '#e0e0e0'
            },
            {
              label: 'Market Return',
              data: [0],
              backgroundColor: '#e0e0e0'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' }
        }
      });
      return;
    }

    this.vsReturnChart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Production',
            data: productionData,
            backgroundColor: '#009688'
          },
          {
            label: 'Market Return',
            data: returnData,
            backgroundColor: '#ef4444'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    });
  }


  private buildWorkforceChart(): void {
    const canvas = document.getElementById('prodWorkforceChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('prodWorkforceChart canvas not found');
      return;
    }
    if (this.workforceChart) {
      this.workforceChart.destroy();
    }

    const labels = this.workforceDistribution.map(w => w.label);
    const data = this.workforceDistribution.map(w => w.value);
    const colors = this.workforceDistribution.map(w => w.color);

    if (data.length === 0 || data.every(v => v === 0)) {
      this.workforceChart = new Chart(canvas.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['No Data'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' }
        }
      });
      return;
    }

    this.workforceChart = new Chart(canvas.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors.length > 0 ? colors : ['#009688', '#4f46e5', '#f59e0b']
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        cutoutPercentage: 60
      }
    });
  }


  private destroyCharts(): void {
    [this.shedChart, this.trendChart, this.vsReturnChart, this.workforceChart]
      .forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
  }
}